import React, { useState, useEffect } from 'react';
import CSTop from '../CS/CSTop';
import InputSearch from '../common/InputSearch';
import HButton from '../common/HButton';
import styled from 'styled-components';
import DevicePagination from './DevicePagination';

const Content = styled.div`
  margin: 0 auto;
  width: 1250px;
  min-width: 1250px;
  padding: 64px 40px 40px 40px;

  table.device {
    border-collapse: collapse;
    text-align: center;
    line-height: 1.5;
    width: 100%;
    margin-bottom: 32px;
    color: #212529;
  }

  table.device thead {
    background-color: white;
  }
  table.device thead th {
    min-width: 50px;
  }
  table.device th {
    font-family: 'Source Sans Pro';
    font-weight: 600;
    font-size: 15px;
    color: #59616a;
    vertical-align: middle !important;
  }

  table.device td,
  table.device th {
    padding: 0.75rem;
    border: 1px solid #e7e9ea;
  }

  table.device tbody {
    vertical-align: middle;
  }
  table.device td {
    font-family: 'Source Sans Pro';
    font-size: 15px;
    color: rgba(48, 57, 69, 0.8);
  }
  
  input[type='checkbox'] {
    background-image: url(${process.env
      .PUBLIC_URL}/images/settings/icon-24-checkbox-active.svg);
    -webkit-appearance: none;
    -moz-appearance: none;
    width: 24px;
    height: 24px;
    margin: 0;
    outline: none;

    &:checked {
      background-image: url(${process.env.PUBLIC_URL}/${(props) =>
        props.theme.images}/settings/icon-24-checkbox-selected.svg);
    }
    &:disabled {
      background-image: url(${process.env
        .PUBLIC_URL}/images/settings/icon-24-checkbox-disabled.svg);
    }
    &:checked:disabled {
      background-image: url(${process.env
        .PUBLIC_URL}/images/settings/icon-24-checked-disabled.svg);
    }
  }
`;

const TableTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-family: 'Source Sans Pro';
  font-weight: 600;
  font-size: 25px;
  color: #59616a;
  margin-bottom: 24px;
`;

const C4WRegistration = () => {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [orderBy, setOrderBy] = useState('created_at asc');
  const [loading, setLoading] = useState(false);
  const [registrations, setRegistrations] = useState({ results: [], pageInfo: { total: 0 } });
  const [featEnableChanges, setFeatEnableChanges] = useState({});

  const onFeatEnableChange = (udi, checked) => {
    setFeatEnableChanges((prev) => {
      const original = registrations.results.find(r => r.udi === udi);
      if (!original) return prev;
      if (!!original.feat_enable === checked) {
        const { [udi]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [udi]: checked };
    });
  };
  const hasChanges = Object.keys(featEnableChanges).length > 0;

  const onSaveFeatEnable = async () => {
    setLoading(true);
    try {
      const changes = Object.entries(featEnableChanges).map(([udi, feat_enable]) => ({
        udi,
        feat_enable,
      }));
  
      const token = localStorage.getItem('token'); 
  
      const response = await fetch('/heclore/v1/c4w/registrations/feat_enable/', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`, 
        },
        body: JSON.stringify({ updates: changes }),
      });
  
      if (!response.ok) {
        throw new Error('Failed to update feat_enable');
      }
  
      setFeatEnableChanges({});
      fetch('/heclore/v1/c4w/registrations/')
        .then(res => res.json())
        .then(data => {
          setRegistrations({
            results: data.results || data,
            pageInfo: data.pageInfo || { total: (data.results || data).length },
          });
          setLoading(false);
        });
    } catch (error) {
      setLoading(false);
      alert('Error saving changes: ' + error.message);
    }
  };


  useEffect(() => {
    setLoading(true);

    const params = new URLSearchParams();
    if (search) params.append('search', search);
    if (orderBy) params.append('orderBy', orderBy);
    if (page) params.append('page', page);

    fetch(`/heclore/v1/c4w/registrations/?${params.toString()}`)
      .then(res => {
        if (!res.ok) {
          console.error('Fetch error! Status:', res.status); 
          throw new Error(`HTTP error! status: ${res.status}`);
        }
        return res.json();
      })
      .then(data => {
        setRegistrations({
          results: data.results || data, 
          pageInfo: data.pageInfo || { total: (data.results || data).length },
        });
        setLoading(false);
      })
      .catch(error => {
        console.error("Detailed error fetching registrations:", error); 
        setLoading(false);
      });
  }, [search, orderBy, page]);


  const onSetPage = (newPage) => setPage(newPage);


  const filteredRegistrations = registrations && Array.isArray(registrations.results)
    ? registrations.results.filter(
        (reg) =>
          reg.reg_key?.toLowerCase().includes(search.toLowerCase()) ||
          reg.udi?.toLowerCase().includes(search.toLowerCase()),
      )
    : [];

  const onSearchEnter = (value) => {
    setSearch(value);
  };

const onClickDownloadExcel = async () => {
  const xlsx = await import('xlsx');
  const FileSaver = await import('file-saver');
  
  try {
    const params = new URLSearchParams();
    if (search) params.append('search', search);
    params.append('all', 'true');
    
    const response = await fetch(`/heclore/v1/c4w/registrations/?${params.toString()}`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    const allRegistrations = data.results || data;

    const ws = xlsx.utils.aoa_to_sheet([
      ['UDI', 'Feat_Enable', 'Licence Status'],
      ...allRegistrations.map((reg) => [
        reg.udi,
        reg.feat_enable ? 'Enabled' : 'Disabled',
        reg.licence_status === true ? 'Active' : 'Inactive',
      ]),
    ]);
    
    const wb = { Sheets: { data: ws }, SheetNames: ['data'] };
    const excelBuffer = xlsx.write(wb, { bookType: 'xlsx', type: 'array' });
    const fileData = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8',
    });
    FileSaver.saveAs(fileData, 'C4W_Registrations.xlsx');
  } catch (error) {
    console.error('Error downloading excel:', error);
    alert('Failed to download Excel file');
  }
};

  return (
    <>
      <CSTop />
      <Content>
        <TableTop>
          <div>C4W Registration</div>
          <div style={{ display: 'flex', gap: '16px' }}>
            <HButton
              category="Tertiary"
              size="Medium"
              text={'Download Excel File'.toUpperCase()}
              onClick={onClickDownloadExcel}
            />
          </div>
        </TableTop>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: '16px', marginBottom: '24px', width: '100%', justifyContent: 'space-between' }}>
          <InputSearch
            size="Medium"
            placeholder={'Search'}
            width="355px"
            value={search}
            onSearchEnterPress={onSearchEnter}
            onChange={(e) => setSearch(e.target.value)}
          />
          <div style={{ display: 'flex', gap: '16px' }}>
            <HButton
              category="Primary"
              size="Medium"
              text={'C4W Registration'.toUpperCase()}
              onClick={() => {
                window.location.href = '/adm/c4wRegistrationUpload';
              }}
            />
            {hasChanges && (
              <HButton
                category="Primary"
                size="Medium"
                text={'Save'.toUpperCase()}
                onClick={onSaveFeatEnable}
              />
            )}
          </div>
        </div>
        {registrations && registrations.pageInfo && (
          <div
            style={{
              float: 'right',
              height: '20px',
              display: 'flex',
              alignItems: 'center',
              fontFamily: 'Source Sans Pro',
              fontSize: '15px',
              color: '#303945cc',
              marginBottom: '15px',
            }}
          >
            Total Registrations: {registrations.pageInfo.total}
          </div>
        )}
        <table className="device">
          <thead>
            <tr>
              <th>UDI</th>
              <th>Feat Enabled</th>
              <th>Licence Status</th>
            </tr>
          </thead>
          <tbody>
          {loading ? (
            <tr>
              <td colSpan={3}>Loading...</td>
            </tr>
          ) : filteredRegistrations.length > 0 ? (
            filteredRegistrations.map((reg) => (
              <tr key={reg.udi}>
                <td>{reg.udi}</td>
                <td>
                  <input
                    type="checkbox"
                    checked={
                      featEnableChanges.hasOwnProperty(reg.udi)
                        ? featEnableChanges[reg.udi]
                        : !!reg.feat_enable
                    }
                    onChange={e =>
                      onFeatEnableChange(reg.udi, e.target.checked)
                    }
                  />
                </td>
                <td>
                  <input
                    type="checkbox"
                    checked={reg.licence_status === 'used'}
                    disabled
                  />
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={3}>No registrations found.</td>
            </tr>
          )}
        </tbody>
        </table>
        {registrations && registrations.pageInfo && (
          <DevicePagination pageInfo={registrations.pageInfo} paginate={onSetPage} />
        )}
      </Content>
    </>
  );
};

export default C4WRegistration;